/* eslint-disable no-param-reassign */

import cloneDeep from 'lodash.clonedeep';

import { QueryBuilderModel } from 'bones/components/molecules/QueryBuilder/types/model';
import { QueryBuilderCopy } from 'bones/components/molecules/QueryBuilder/types/schema';
import { RDFS_LABEL_IRI } from 'bones/constants/iris';
import {
  ANY_CLASS_IRI,
  Direction,
  OperatorType,
} from 'bones/utils/queryBuilder/constants';
import { DraftTopExpression } from 'bones/utils/queryBuilder/types';
import { getClassifyClassesByIri } from 'bones/utils/queryBuilder/validation/getClassifyClassesByIri';
import { subGraphsValidator } from 'bones/utils/queryBuilder/validation/validateSubGraphs';
import { validateSubject } from 'bones/utils/queryBuilder/validation/validateSubject';
import { validateValue } from 'bones/utils/queryBuilder/validation/validateValue';

export const updateValidation = ({
  copy,
  topExpression: prevTopExpression,
  skipOperator,
  validVariables,
  validModel,
}: {
  copy: QueryBuilderCopy;
  topExpression: DraftTopExpression;
  skipOperator?: boolean;
  validVariables?: Record<string, string[]>;
  validModel?: QueryBuilderModel;
}): {
  topExpression: DraftTopExpression;
  isValid: boolean;
} => {
  let isValid = true;
  const topExpression = cloneDeep(prevTopExpression);
  const classifyClassesByIri = getClassifyClassesByIri(topExpression);

  topExpression.expressions.forEach((subExpression, subExpressionIdx) => {
    subExpression.expressions.forEach((expression, expressionIdx) => {
      const validateVariable = subGraphsValidator(
        subExpression.expressions.filter(
          (_, idx) => idx === 0 || idx === expressionIdx
        )
      );
      expression.filters.forEach((filter, filterIdx) => {
        // reset invalid state
        filter.isInvalid = {
          subject: false,
          property: false,
          operator: false,
          comparisonSubject: false,
          comparisonProperty: false,
          value: false,
        };
        filter.tooltip = {
          subject: '',
          property: '',
          comparisonSubject: '',
          comparisonProperty: '',
          value: '',
        };

        const { isValid: isSubjectValid, tooltip: subjectTooltip } =
          validateSubject({
            validateVariable,
            subject: filter.subject,
            filter,
            topExpression,
            expressionIdx,
            subExpressionIdx,
            filterIdx,
            validVariables,
            validModel,
            copy,
          });
        if (!isSubjectValid) {
          isValid = false;
          filter.isInvalid.subject = true;
          filter.tooltip.subject = subjectTooltip;
        }

        if (filter.operator && !filter.operator.comparison) {
          const { isValid: isValueValid, tooltip: valueTooltip } =
            validateValue({
              operator: filter.operator,
              value: filter.value,
              topExpression,
              expressionIdx,
              subExpressionIdx,
              filterIdx,
              validVariables,
              validModel,
              classifyClassesByIri,
              copy,
            });
          if (!isValueValid) {
            isValid = false;
            filter.isInvalid.value = true;
            filter.tooltip.value = valueTooltip;
          }
        }

        if (
          validModel &&
          filter.operator &&
          filter.operator.operatorType !== OperatorType.INSTANCE &&
          filter.operator.iri &&
          filter.operator.iri !== RDFS_LABEL_IRI
        ) {
          if (!validModel.propertiesByIri[filter.operator.iri]) {
            isValid = false;
            filter.isInvalid.property = true;
            filter.tooltip.property = copy.error.propertyModelUndefined;
          } else {
            const domainsToCheck = new Set(
              validModel.propertiesByIri[filter.operator.iri].domains
            );
            const rangesToCheck = new Set(
              validModel.propertiesByIri[filter.operator.iri].ranges
            );
            Object.entries(classifyClassesByIri).forEach(
              ([classIri, classifyClasses]) => {
                Array.from(classifyClasses).forEach((classifyIri) => {
                  if (domainsToCheck.has(classifyIri)) {
                    domainsToCheck.add(classIri);
                  }
                  if (rangesToCheck.has(classifyIri)) {
                    rangesToCheck.add(classIri);
                  }
                });
              }
            );
            if (
              isSubjectValid &&
              filter.subject &&
              filter.subject.iri !== ANY_CLASS_IRI &&
              (filter.operator.operatorType === OperatorType.DATATYPE ||
                filter.operator.direction === Direction.OUTBOUND) &&
              domainsToCheck.size &&
              !domainsToCheck.has(filter.subject.iri)
            ) {
              isValid = false;
              filter.isInvalid.property = true;
              filter.tooltip.property = copy.error.propertyModelDomainOrRange;
            } else if (
              isSubjectValid &&
              filter.subject &&
              filter.subject.iri !== ANY_CLASS_IRI &&
              filter.operator.direction === Direction.INBOUND &&
              rangesToCheck.size &&
              !rangesToCheck.has(filter.subject.iri)
            ) {
              isValid = false;
              filter.isInvalid.property = true;
              filter.tooltip.property = copy.error.propertyModelDomainOrRange;
            }
          }
        }

        if (validModel && filter.operator && filter.operator.comparison) {
          if (
            filter.comparisonSubject &&
            filter.comparisonSubject.iri !== ANY_CLASS_IRI &&
            !validModel.classesByIri[filter.comparisonSubject.iri]
          ) {
            isValid = false;
            filter.isInvalid.comparisonSubject = true;
            filter.tooltip.comparisonSubject = copy.error.classModelUndefined;
          }
          if (
            filter.comparisonOperator &&
            filter.comparisonOperator.iri !== RDFS_LABEL_IRI &&
            !validModel.propertiesByIri[filter.comparisonOperator.iri]
          ) {
            isValid = false;
            filter.isInvalid.comparisonProperty = true;
            filter.tooltip.comparisonProperty =
              copy.error.propertyModelUndefined;
          } else if (
            filter.comparisonOperator &&
            filter.comparisonOperator.iri !== RDFS_LABEL_IRI &&
            filter.comparisonSubject &&
            filter.comparisonSubject.iri !== ANY_CLASS_IRI &&
            validModel.propertiesByIri[filter.comparisonOperator.iri].domains
              .size &&
            !validModel.propertiesByIri[
              filter.comparisonOperator.iri
            ].domains.has(filter.comparisonSubject.iri)
          ) {
            isValid = false;
            filter.isInvalid.comparisonProperty = true;
            filter.tooltip.comparisonProperty =
              copy.error.propertyModelDomainOrRange;
          }
        }

        // ignore operator validation
        if (skipOperator) {
          return;
        }

        if (!filter.operator) {
          isValid = false;
          filter.isInvalid.property = true;
        }
        if (!filter.operator?.type) {
          isValid = false;
          filter.isInvalid.operator = true;
        }

        if (filter.operator?.comparison && !filter.comparisonSubject) {
          isValid = false;
          filter.isInvalid.comparisonSubject = true;
        }
        if (filter.operator?.comparison && !filter.comparisonOperator) {
          isValid = false;
          filter.isInvalid.comparisonProperty = true;
        }
      });
    });
  });

  return { topExpression, isValid };
};
